import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { GET_AWS_RESOURCE_BY_CHECK_ID, GET_AWS_CHECK_EXCEPTIONS, CREATE_AWS_CHECK_EXCEPTION, DELETE_AWS_CHECK_EXCEPTION } from "../../../../apis/organization-apis/Infra-apis/InfraApis";
import InfraResourcesTabBase from "./shared/InfraResourcesTabBase";
import { getDateRangeInUTC } from "../../../../utils/dateUtils";

const AWSResourcesTab = ({ activeCheckId, onRefetch, onStartPolling, globalException, isTriggerDisabled, checkStatus, remediationEnabled = false, isPollingRunStatus, onRegisterRefetch }) => {
  const [selectedDate, setSelectedDate] = useState(null);

  const { data: awsResourcesData, loading: awsResourcesLoading, refetch: refetchAwsResources } = useQuery(GET_AWS_RESOURCE_BY_CHECK_ID, {
    variables: {
      check_id: Number(activeCheckId),
      ...(selectedDate ? getDateRangeInUTC(selectedDate) : {}),
    },
    skip: !activeCheckId,
  });

  const { data: awsExceptionsData, refetch: refetchAwsExceptions } = useQuery(GET_AWS_CHECK_EXCEPTIONS, {
    variables: { filter: { active_check_id: Number(activeCheckId) }, page: 1, limit: 10000 },
    skip: !activeCheckId,
  });

  React.useEffect(() => {
    if (onRegisterRefetch) {
      onRegisterRefetch(async () => {
        await refetchAwsResources();
        await refetchAwsExceptions();
      });
    }
  }, [onRegisterRefetch, refetchAwsResources, refetchAwsExceptions]);

  const [createAwsException, { loading: creatingException }] = useMutation(CREATE_AWS_CHECK_EXCEPTION);
  const [deleteAwsException] = useMutation(DELETE_AWS_CHECK_EXCEPTION);

  const mapResource = React.useCallback((resourcesData, exceptions) => {
    if (!resourcesData?.getAwsResourceByCheckId) return [];
    const hasGlobalException = !!globalException;
    return resourcesData.getAwsResourceByCheckId.map((res) => {
      const resourceId = res?.resource_id != null ? String(res.resource_id) : "";
      const accountId = res?.account_id ? String(res.account_id) : null;
      const selectionId = accountId ? `${resourceId}::${accountId}` : resourceId;
      const resourceException = exceptions.find((exc) => {
        const excResourceId = String(exc?.resource_id || "");
        const excAccountId = exc?.account_id ? String(exc.account_id) : null;
        return excResourceId === resourceId && excAccountId === accountId && exc?.is_active === true;
      });
      const hasException = !!resourceException;
      return {
        ...res,
        id: res?.id,
        resourceId,
        selectionId,
        name: res?.resource_name,
        account: res?.account_id,
        status: hasGlobalException || hasException ? "disabled" : (res?.status || "Unknown"),
        reason: hasGlobalException 
          ? "-" 
          : (hasException ? (resourceException?.reason || "-") : (res?.status_reason || "-")),
        exceptionId: resourceException?.id || null,
        exceptionData: resourceException || null,
        exceptionValidUntil: hasException && resourceException?.exception_valid_till ? resourceException.exception_valid_till : null,
      };
    });
  }, [globalException]);

  const awsResources = awsResourcesData?.getAwsResourceByCheckId || [];

  const accountFilterOptions = React.useMemo(() => {
    const accounts = new Set();
    awsResources.forEach((res) => {
      if (res?.account_id) {
        accounts.add(String(res.account_id));
      }
    });
    return Array.from(accounts).map((account) => ({
      id: account,
      label: account,
    }));
  }, [awsResources]);

  const getAccountId = React.useCallback((resource) => resource?.account_id || resource?.account, []);

  const columns = React.useMemo(() => [
    { label: "Resource Name", field: "name", align: "left", width: "14%" },
    { label: "Resource ID", field: "resourceId", align: "left", width: "14%" },
    {
      label: "Account ID",
      field: "account",
      align: "left",
      width: "14%",
      filterConfig: accountFilterOptions.length ? { filterOptions: accountFilterOptions } : undefined,
    },
    { label: "Latest Execution Time", field: "policy_execution_time", align: "left", width: "14%" },
  ], [accountFilterOptions]);

  return (
    <InfraResourcesTabBase
      providerName="Aws"
      activeCheckId={activeCheckId}
      onRefetch={onRefetch}
      onStartPolling={onStartPolling}
      resourcesData={awsResourcesData}
      resourcesLoading={awsResourcesLoading}
      exceptionsData={awsExceptionsData?.getAwsCheckExceptions}
      refetchExceptions={refetchAwsExceptions}
      refetchResources={refetchAwsResources}
      createException={createAwsException}
      creatingException={creatingException}
      deleteException={deleteAwsException}
      mapResource={mapResource}
      columns={columns}
      getAccountId={getAccountId}
      globalException={globalException}
      isTriggerDisabled={isTriggerDisabled}
      checkStatus={checkStatus}
      remediationEnabled={remediationEnabled}
      isPollingRunStatus={isPollingRunStatus}
      selectedDate={selectedDate}
      onDateChange={setSelectedDate}
    />
  );
};

export default AWSResourcesTab;

